import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { supabase } from '../lib/supabase';
import StarRating from '../components/StarRating';
import LoadingSpinner from '../components/LoadingSpinner';
import { BadgeFeedItem } from '../components/BadgeFeedItem';
import NearbyCheckInButton from '../components/NearbyCheckInButton';
import {
  Users,
  UserPlus,
  UserMinus,
  UserCheck,
  Search,
  Star,
  MapPin,
  Heart,
  X,
  Check,
  Clock,
  MessageCircle,
  ChevronRight,
  ChevronDown,
  Send,
  Filter,
  DollarSign,
  Martini,
  Zap,
  Wine,
  Image as ImageIcon,
  Beer,
  Calendar,
  LogIn,
  Flag
} from 'lucide-react';
import ReportModal from '../components/ReportModal';

// Sub-rating display component with icons matching bar detail page
function SubRatings({ rating, compact = false }) {
  // Handle both snake_case (from DB) and camelCase (from local state)
  // Use shorter labels for mobile
  const subRatings = [
    { key: 'price', label: 'Price', shortLabel: 'Price', icon: DollarSign, color: 'text-dive-green', value: rating.price },
    { key: 'ambiance', label: 'Ambiance', shortLabel: 'Vibe', icon: Martini, color: 'text-dive-blue', value: rating.ambiance },
    { key: 'crowd', label: 'Crowd', shortLabel: 'Crowd', icon: Users, color: 'text-dive-amber', value: rating.crowd },
    { key: 'service', label: 'Service', shortLabel: 'Svc', icon: Zap, color: 'text-dive-neon', value: rating.service },
    { key: 'drinks', label: 'Drinks', shortLabel: 'Drinks', icon: Wine, color: 'text-purple-400', value: rating.drink_selection ?? rating.drinkSelection }
  ].filter(r => r.value != null);

  if (subRatings.length === 0) return null;

  if (compact) {
    return (
      <div className="grid grid-cols-5 gap-1 mt-3">
        {subRatings.map(({ key, label, shortLabel, icon: Icon, color, value }) => (
          <div key={key} className="bg-dive-darker border border-dive-gray rounded-lg p-1.5 text-center min-w-0">
            <Icon className={`w-3 h-3 sm:w-4 sm:h-4 ${color} mx-auto mb-0.5`} />
            <div className="text-white text-xs sm:text-sm font-bold">{value.toFixed(1)}</div>
            <div className="text-gray-500 text-[10px] sm:text-xs">
              <span className="sm:hidden">{shortLabel}</span>
              <span className="hidden sm:inline">{label}</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-5 gap-2 mt-3">
      {subRatings.map(({ key, label, shortLabel, icon: Icon, color, value }) => (
        <div key={key} className="bg-dive-darker border border-dive-gray rounded-lg p-2 sm:p-3 text-center min-w-0">
          <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${color} mx-auto mb-1`} />
          <div className="text-white text-sm sm:text-lg font-bold">{value.toFixed(1)}</div>
          <div className="text-gray-500 text-[10px] sm:text-xs">
            <span className="sm:hidden">{shortLabel}</span>
            <span className="hidden sm:inline">{label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function CheckInFeedItem({ item }) {
  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-dive-dark border border-amber-500/30 rounded-xl p-4">
      <div className="flex items-start gap-3">
        <Link to={`/social/${item.user_id}`} className="flex-shrink-0">
          {item.avatar_url ? (
            <img src={item.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-dive-amber/20 flex items-center justify-center">
              <Beer className="w-5 h-5 text-dive-amber" />
            </div>
          )}
        </Link>
        <div className="min-w-0 flex-1">
          <p className="text-white">
            <Link to={`/social/${item.user_id}`} className="font-medium hover:underline">
              {item.display_name}
            </Link>
            <span className="text-gray-400 text-sm"> · {timeAgo(item.created_at)}</span>
          </p>
          <p className="text-dive-amber mt-1">{item.message_text}</p>
          <Link to={`/bar/${item.bar_id}`} className="text-sm text-gray-400 hover:text-white mt-2 inline-block">
            {item.bar_name} {item.bar_neighborhood ? `· ${item.bar_neighborhood}` : ''}
          </Link>
        </div>
      </div>
    </div>
  );
}

// Enhanced Friend Feed Item Component with "My Bars" style
function FeedItem({ item, bars, photos, showSubRatings = true }) {
  const [expanded, setExpanded] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const { getFriendRatingComments, addFriendRatingComment } = useApp();
  const { user } = useAuth();

  // Get photos for this bar uploaded by the rating user ONLY (no fallback)
  const userBarPhotos = useMemo(() => {
    if (!photos || !item.bar_id || !item.user_id) return [];
    return photos.filter(p => p.barId === item.bar_id && p.userId === item.user_id);
  }, [photos, item.bar_id, item.user_id]);

  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const loadComments = async () => {
    if (!showComments) {
      setLoadingComments(true);
      try {
        const data = await getFriendRatingComments(item.rating_id);
        setComments(data);
      } catch (err) {
        console.error('Error loading comments:', err);
      } finally {
        setLoadingComments(false);
      }
    }
    setShowComments(!showComments);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    try {
      await addFriendRatingComment(item.rating_id, commentText.trim());
      const data = await getFriendRatingComments(item.rating_id);
      setComments(data);
      setCommentText('');
    } catch (err) {
      console.error('Error posting comment:', err);
    }
  };

  return (
    <div className="bg-dive-dark border border-dive-gray rounded-xl overflow-hidden">
      {/* Header with user info and bar name */}
      <div className="p-4 flex items-start gap-3">
        <Link to={`/social/${item.user_id}`} className="flex-shrink-0">
          {item.avatar_url ? (
            <img
              src={item.avatar_url}
              alt={item.display_name}
              className="w-10 h-10 rounded-full object-cover border-2 border-dive-neon/30"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-dive-neon/20 flex items-center justify-center border-2 border-dive-neon/30">
              <span className="text-dive-neon font-medium">
                {(item.display_name || 'U').charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </Link>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Link to={`/social/${item.user_id}`} className="text-white font-medium hover:text-dive-neon">
              {item.display_name || item.username}
            </Link>
            <span className="text-gray-500 text-sm">rated</span>
            <Link to={`/bar/${item.bar_id}`} className="text-dive-amber font-medium hover:underline truncate">
              {item.bar_name}
            </Link>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-gray-500 text-xs">{timeAgo(item.created_at)}</span>
            <span className="text-gray-600">•</span>
            <div className="flex items-center gap-1 text-gray-500 text-xs">
              <MapPin className="w-3 h-3" />
              <span>{item.bar_neighborhood}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bar Rating Card */}
      <Link to={`/bar/${item.bar_id}`} className="block mx-4 mb-4">
        <div className="bg-dive-darker border border-dive-gray rounded-xl overflow-hidden hover:border-dive-neon/50 transition-colors">
          <div className="p-4">
            {/* Rating with Voted Dive badge */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center gap-1 bg-amber-500/20 text-amber-400 px-3 py-1.5 rounded-lg">
                <Star className="w-4 h-4 fill-amber-400" />
                <span className="font-bold text-lg">{item.overall?.toFixed(1) || '?'}</span>
              </div>
              {item.is_dive_bar && (
                <span className="text-xs px-2 py-1 border border-amber-400 text-amber-400 rounded-full whitespace-nowrap">
                  Voted Dive
                </span>
              )}
            </div>

            {/* Sub-ratings */}
            {showSubRatings && <SubRatings rating={item} compact />}
          </div>
        </div>
      </Link>

      {/* Review Text */}
      {item.review_text && (
        <div className="px-4 pb-3">
          <div className="flex items-start gap-2">
            <MessageCircle className="w-4 h-4 text-dive-amber mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className={`text-gray-300 text-sm ${!expanded && 'line-clamp-3'}`}>
                "{item.review_text}"
              </p>
              {item.review_text.length > 150 && (
                <button
                  onClick={(e) => { e.preventDefault(); setExpanded(!expanded); }}
                  className="text-dive-neon text-xs hover:underline mt-1"
                >
                  {expanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>
            {item.review_visibility === 'friends' && (
              <span className="flex items-center gap-1 text-xs text-blue-400" title="Friends only">
                <Users className="w-3 h-3" />
              </span>
            )}
          </div>
        </div>
      )}

      {/* Photos from this user for this bar - underneath rating in distinct boxes */}
      {userBarPhotos.length > 0 && (
        <div className="px-4 pb-3">
          <div className="flex items-center gap-2 mb-2 text-gray-400 text-sm">
            <ImageIcon className="w-4 h-4" />
            <span>Photos by {item.display_name}</span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {userBarPhotos.slice(0, 4).map((photo, idx) => (
              <div key={photo.id || idx} className="aspect-square border border-dive-gray rounded-lg overflow-hidden">
                <img
                  src={photo.url}
                  alt={`${item.bar_name} photo`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-4 py-2 border-t border-dive-gray flex items-center gap-4">
        <button
          onClick={loadComments}
          className="flex items-center gap-1.5 text-gray-400 hover:text-white text-sm"
        >
          <MessageCircle className="w-4 h-4" />
          <span>{item.comment_count || 0} comments</span>
        </button>
        <Link
          to={`/bar/${item.bar_id}`}
          className="flex items-center gap-1 text-gray-400 hover:text-dive-amber text-sm ml-auto"
        >
          View bar <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="border-t border-dive-gray bg-dive-darker">
          {loadingComments ? (
            <div className="p-4 text-center text-gray-500 text-sm">Loading comments...</div>
          ) : (
            <>
              {comments.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No comments yet. Be the first!
                </div>
              ) : (
                <div className="divide-y divide-dive-gray">
                  {comments.map(comment => (
                    <div key={comment.id} className="p-3 flex gap-2">
                      <Link to={`/social/${comment.user_id}`} className="flex-shrink-0">
                        {comment.profiles?.avatar_url ? (
                          <img
                            src={comment.profiles.avatar_url}
                            alt=""
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-dive-neon/10 flex items-center justify-center">
                            <span className="text-dive-neon text-xs">
                              {(comment.profiles?.display_name || 'U').charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                      </Link>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm font-medium">
                            {comment.profiles?.display_name || 'User'}
                          </span>
                          <span className="text-gray-500 text-xs">
                            {timeAgo(comment.created_at)}
                          </span>
                        </div>
                        <p className="text-gray-300 text-sm">{comment.comment_text || comment.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Comment Form */}
              {user && (
                <form onSubmit={handleSubmitComment} className="p-3 flex gap-2">
                  <input
                    type="text"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Write a comment..."
                    className="flex-1 bg-dive-dark border border-dive-gray rounded-lg px-3 py-2 text-sm"
                  />
                  <button
                    type="submit"
                    disabled={!commentText.trim()}
                    className="p-2 bg-dive-neon text-white rounded-lg disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Friend Request Card Component
function FriendRequestCard({ request, onAccept, onDecline, isIncoming }) {
  const [loading, setLoading] = useState(false);
  const user = isIncoming ? request.from_user : request.to_user;

  const handleAccept = async () => {
    setLoading(true);
    try {
      await onAccept(request.id);
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    setLoading(true);
    try {
      await onDecline(request.id);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-dive-dark border border-dive-gray rounded-xl p-4 flex items-center gap-3">
      <div className="w-12 h-12 rounded-full bg-dive-neon/20 flex items-center justify-center flex-shrink-0">
        {user?.avatar_url ? (
          <img src={user.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
        ) : (
          <span className="text-dive-neon font-medium text-lg">
            {(user?.display_name || 'U').charAt(0).toUpperCase()}
          </span>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium truncate">{user?.display_name || 'User'}</p>
        <p className="text-gray-500 text-sm">@{user?.username}</p>
      </div>

      {isIncoming ? (
        <div className="flex gap-2">
          <button
            onClick={handleDecline}
            disabled={loading}
            className="p-2 bg-dive-gray text-gray-400 rounded-lg hover:bg-red-500/20 hover:text-red-400 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            onClick={handleAccept}
            disabled={loading}
            className="p-2 bg-dive-neon text-white rounded-lg hover:bg-dive-neon/80 transition-colors"
          >
            <Check className="w-5 h-5" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Clock className="w-4 h-4" />
          <span>Pending</span>
        </div>
      )}
    </div>
  );
}

// My Feed Tab - Shows user's bars with friends' comments (My Bars style)
function MyFeedTab({ bars, ratings, user, photos, comments: allComments }) {
  const { getFriendRatingComments, addFriendRatingComment, users } = useApp();
  const [myFeedItems, setMyFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState({});

  useEffect(() => {
    loadMyFeed();
  }, [user, ratings, allComments]);

  const loadMyFeed = async () => {
    if (!user) return;
    setLoading(true);
    
    try {
      // Get all my ratings
      const myRatings = ratings
        .filter(r => r.userId === user.id)
        .sort((a, b) => b.createdAt - a.createdAt);
      
      // Load comments for each rating
      const ratingsWithComments = await Promise.all(
        myRatings.map(async (rating) => {
          const bar = bars.find(b => b.id === rating.barId);
          if (!bar) return null;
          
          // Get my photos for this bar
          const myPhotos = photos?.filter(p => p.barId === rating.barId && p.userId === user.id) || [];
          
          // Get my review for this bar (from comments where is_review = true)
          const myReview = allComments?.find(
            c => c.barId === rating.barId && c.userId === user.id && c.isReview
          );
          
          try {
            const friendComments = await getFriendRatingComments(rating.id);
            return {
              type: 'rating',
              rating,
              bar,
              photos: myPhotos,
              review: myReview,
              comments: friendComments || [],
              created_at: rating.createdAt
            };
          } catch {
            return {
              type: 'rating',
              rating,
              bar,
              photos: myPhotos,
              review: myReview,
              comments: [],
              created_at: rating.createdAt
            };
          }
        })
      );
      
      // Also fetch my badges (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data: myBadges, error: badgeError } = await supabase
        .from('user_badges')
        .select(`
          id,
          user_id,
          earned_at,
          badges (
            id,
            name,
            description,
            icon
          )
        `)
        .eq('user_id', user.id)
        .gte('earned_at', thirtyDaysAgo.toISOString())
        .order('earned_at', { ascending: false });
      
      // Transform badges for the feed
      const badgeItems = [];
      if (!badgeError && myBadges) {
        const currentUser = users.find(u => u.id === user.id);
        myBadges.forEach(b => {
          badgeItems.push({
            type: 'badge',
            id: b.id,
            user_id: b.user_id,
            earned_at: b.earned_at,
            created_at: new Date(b.earned_at),
            badge_name: b.badges?.name,
            badge_description: b.badges?.description,
            badge_icon: b.badges?.icon,
            display_name: currentUser?.displayName || 'You',
            username: currentUser?.username || 'you',
            avatar_url: currentUser?.avatarUrl
          });
        });
      }
      
      // Combine and sort by date
      const allItems = [...ratingsWithComments.filter(Boolean), ...badgeItems];
      allItems.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setMyFeedItems(allItems);
    } catch (err) {
      console.error('Error loading my feed:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Extract ratings for compatibility with existing render logic
  const myRatingsWithComments = myFeedItems.filter(item => item.type === 'rating');

  const handleSubmitComment = async (ratingId) => {
    const text = commentText[ratingId];
    if (!text?.trim()) return;

    try {
      await addFriendRatingComment(ratingId, text.trim());
      await loadMyFeed(); // Reload to get updated comments
      setCommentText(prev => ({ ...prev, [ratingId]: '' }));
    } catch (err) {
      console.error('Error posting comment:', err);
    }
  };

  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading...</div>;
  }

  if (myFeedItems.length === 0) {
    return (
      <div className="text-center py-12 bg-dive-dark border border-dive-gray rounded-xl">
        <Star className="w-12 h-12 text-dive-gray mx-auto mb-3" />
        <h3 className="text-white text-lg mb-2">No activity yet</h3>
        <p className="text-gray-500 text-sm mb-4">
          Rate some bars to see them here with friends' comments!
        </p>
        <Link to="/list" className="btn-primary inline-flex items-center gap-2">
          <img src="/logo-icon.png" alt="" className="w-4 h-4 object-contain" />
          Browse Bars
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {myFeedItems.map((item) => {
        // Render badge items
        if (item.type === 'badge') {
          return <BadgeFeedItem key={`badge-${item.id}`} item={item} />;
        }
        
        // Render rating items
        const { rating, bar, photos: myPhotos, review, comments } = item;
        return (
        <div key={rating.id} className="bg-dive-dark border border-dive-gray rounded-xl overflow-hidden">
          {/* My Rating - My Bars Style */}
          <Link to={`/bar/${bar.id}`} className="block">
            {/* Bar Photo */}
            {myPhotos.length > 0 ? (
              <div className="h-32 bg-dive-gray overflow-hidden relative">
                <img
                  src={myPhotos[0].url}
                  alt={bar.name}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="h-24 bg-dive-gray/50 flex items-center justify-center">
                <img src="/logo-icon.png" alt="" className="w-8 h-8 object-contain opacity-50" />
              </div>
            )}

            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-display text-lg tracking-wide">{bar.name}</h3>
                  <p className="text-gray-500 text-sm">{bar.neighborhood}</p>
                </div>
              </div>
              
              {/* Rating with Voted Dive badge */}
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center gap-1 bg-amber-500/20 text-amber-400 px-3 py-1.5 rounded-lg">
                  <Star className="w-4 h-4 fill-amber-400" />
                  <span className="font-bold text-lg">{rating.overall.toFixed(1)}</span>
                </div>
                {rating.isDiveBar && (
                  <span className="text-xs px-2 py-1 border border-amber-400 text-amber-400 rounded-full whitespace-nowrap">
                    Voted Dive
                  </span>
                )}
              </div>

              {/* Sub-ratings */}
              <SubRatings rating={rating} compact />

              {/* My Review */}
              {review && (
                <div className="mt-3 bg-dive-dark/50 rounded-lg p-3 border border-dive-gray/50">
                  <div className="flex items-start gap-2">
                    <MessageCircle className="w-4 h-4 text-dive-amber mt-0.5 flex-shrink-0" />
                    <p className="text-gray-300 text-sm flex-1">"{review.text}"</p>
                    {review.visibility === 'friends' && (
                      <span className="flex items-center gap-1 text-xs text-blue-400" title="Friends only">
                        <Users className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                </div>
              )}

              <p className="text-gray-500 text-xs mt-3">
                Rated {timeAgo(rating.createdAt)}
              </p>
            </div>
          </Link>
          
          {/* Friends' Comments */}
          <div className="border-t border-dive-gray">
            <div className="p-3 bg-dive-darker flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-dive-neon" />
              <span className="text-sm text-gray-400">
                {comments.length} friend comment{comments.length !== 1 ? 's' : ''}
              </span>
            </div>
            
            {comments.length > 0 ? (
              <div className="divide-y divide-dive-gray/50">
                {comments.map(comment => (
                  <div key={comment.id} className="p-3 flex items-start gap-3">
                    <Link to={`/social/${comment.user_id}`} className="flex-shrink-0">
                      <div className="w-8 h-8 rounded-full bg-dive-neon/20 flex items-center justify-center overflow-hidden">
                        {comment.profiles?.avatar_url ? (
                          <img
                            src={comment.profiles.avatar_url}
                            alt={comment.profiles.display_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-dive-neon text-xs">
                            {(comment.profiles?.display_name || 'U').charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/social/${comment.user_id}`}
                          className="text-dive-neon text-sm font-medium hover:underline"
                        >
                          {comment.profiles?.display_name || 'Friend'}
                        </Link>
                        <span className="text-gray-600 text-xs">
                          {timeAgo(comment.created_at)}
                        </span>
                      </div>
                      <p className="text-gray-300 text-sm mt-1">{comment.text}</p>
                    </div>
                    {user && comment.user_id !== user.id && (
                      <button
                        onClick={(e) => { e.preventDefault(); setReportingContent({ type: 'feed_comment', id: comment.id, text: comment.text }); }}
                        className="text-gray-600 hover:text-red-400 transition-colors flex-shrink-0 p-1"
                        title="Report comment"
                      >
                        <Flag className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-gray-600 text-sm">
                No comments from friends yet
              </div>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );
}

function Friends() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const {
    users,
    friends,
    followers,
    following,
    followRequests,
    friendRequests,
    ratings,
    comments,
    photos,
    followUser,
    unfollowUser,
    acceptFriendRequest,
    declineFriendRequest,
    cancelFriendRequest,
    removeFriend,
    getFriendStatus,
    getFollowStatus,
    getFriendFeed,
    acceptFollowRequest,
    denyFollowRequest,
    bars,
    loading,
    blockUser,
    unblockUser,
    isUserBlocked,
    blockedUsers
  } = useApp();

  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    // Check URL for tab parameter
    const tabParam = searchParams.get('tab');
    return tabParam === 'myfeed' ? 'myfeed' : 'feed';
  });
  const [friendFeed, setFriendFeed] = useState([]);
  const [friendBadges, setFriendBadges] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [unfriendingUser, setUnfriendingUser] = useState(null);
  const [isUnfriending, setIsUnfriending] = useState(false);
  const [reportingContent, setReportingContent] = useState(null);

  // Filter states for Friend Feed
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState('all');
  const [diveBarOnly, setDiveBarOnly] = useState(false);
  const [timeframe, setTimeframe] = useState('all');

  // React to URL tab parameter changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'myfeed') {
      setActiveTab('myfeed');
    }
  }, [searchParams]);

  // Load friend feed - clear and reload when following changes
  useEffect(() => {
    if (user && activeTab === 'feed') {
      // Clear existing feed first to force re-render
      setFriendFeed([]);
      setFriendBadges([]);
      loadFeed();
    }
  }, [user, activeTab, following]);
  
  // Also reload when friends array changes (backward compat)
  useEffect(() => {
    if (user && activeTab === 'feed' && friends.length > 0) {
      loadFeed();
    }
  }, [friends]);

  const loadFeed = async () => {
    setLoadingFeed(true);
    try {
      // Fetch ratings feed
      const feed = await getFriendFeed(50, 0);
      setFriendFeed(feed);
      
      // Also fetch friend badge achievements (last 30 days)
      if (friends.length > 0) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        const { data: badgeData, error: badgeError } = await supabase
          .from('user_badges')
          .select(`
            id,
            user_id,
            earned_at,
            badges (
              id,
              name,
              description,
              icon
            )
          `)
          .in('user_id', friends)
          .gte('earned_at', thirtyDaysAgo.toISOString())
          .order('earned_at', { ascending: false });
        
        if (!badgeError && badgeData && badgeData.length > 0) {
          // Get profile info from users array (already loaded)
          const transformedBadges = badgeData.map(b => {
            const badgeUser = users.find(u => u.id === b.user_id);
            return {
              type: 'badge',
              id: b.id,
              user_id: b.user_id,
              earned_at: b.earned_at,
              created_at: b.earned_at, // For sorting
              badge_name: b.badges?.name,
              badge_description: b.badges?.description,
              badge_icon: b.badges?.icon,
              display_name: badgeUser?.displayName || 'Unknown',
              username: badgeUser?.username || 'unknown',
              avatar_url: badgeUser?.avatarUrl
            };
          });
          setFriendBadges(transformedBadges);
        }
      }
    } catch (err) {
      console.error('Error loading feed:', err);
    } finally {
      setLoadingFeed(false);
    }
  };

  // Get friend user objects for filter dropdown
  const friendUsers = useMemo(() => 
    users.filter(u => friends.includes(u.id)),
    [users, friends]
  );

  // Combine ratings and badges, apply filters
  const filteredFeed = useMemo(() => {
    // Get current following list - prefer following array, only fallback if undefined
    const currentFollowing = following !== undefined ? following : friends;
    
    // Mark ratings with type and filter by current following, exclude blocked users
    const ratingsWithType = friendFeed
      .filter(r => currentFollowing.includes(r.user_id) && !blockedUsers.includes(r.user_id))
      .map(r => ({ ...r, type: r.type || 'rating' }));
    
    // Filter badges by current following, exclude blocked users
    const filteredBadges = friendBadges.filter(b => currentFollowing.includes(b.user_id) && !blockedUsers.includes(b.user_id));
    
    // Combine ratings and badges
    let combined = [...ratingsWithType, ...filteredBadges];
    
    // Sort by date (newest first)
    combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    // Filter by friend
    if (selectedFriend !== 'all') {
      combined = combined.filter(item => item.user_id === selectedFriend);
    }

    // Filter by dive bar (only applies to ratings)
    if (diveBarOnly) {
      combined = combined.filter(item => item.type === 'badge' || item.type === 'check_in' || item.is_dive_bar);
    }

    // Filter by timeframe
    if (timeframe !== 'all') {
      const now = new Date();
      let cutoff;
      switch (timeframe) {
        case '24h':
          cutoff = new Date(now - 24 * 60 * 60 * 1000);
          break;
        case 'week':
          cutoff = new Date(now - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          cutoff = new Date(now - 30 * 24 * 60 * 60 * 1000);
          break;
        default:
          cutoff = null;
      }
      if (cutoff) {
        combined = combined.filter(item => new Date(item.created_at) >= cutoff);
      }
    }

    return combined;
  }, [friendFeed, friendBadges, selectedFriend, diveBarOnly, timeframe, blockedUsers]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-dive-black py-6 px-4">
        <div className="max-w-2xl mx-auto text-center py-12">
          <Users className="w-16 h-16 text-dive-gray mx-auto mb-4" />
          <h2 className="text-white text-xl mb-2">Sign In Required</h2>
          <p className="text-gray-400 text-sm mb-6">
            You need to be logged in to follow others and see their ratings
          </p>
          <Link to="/auth" className="btn-primary inline-flex items-center gap-2">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  // Search results (exclude current user, show everyone including people you follow)
  const normalizedSearch = searchQuery.trim().replace(/^@/, '').toLowerCase();
  const searchResults = normalizedSearch.length >= 2
    ? users.filter(u =>
        u.id !== user.id &&
        (u.username.toLowerCase().includes(normalizedSearch) ||
         u.displayName.toLowerCase().includes(normalizedSearch))
      )
    : [];

  const handleFollow = async (userId) => {
    try {
      await followUser(userId);
      setSearchQuery('');
    } catch (error) {
      console.error('Error following user:', error);
      alert(error.message || 'Failed to follow user');
    }
  };

  const handleRemoveFriend = async () => {
    if (!unfriendingUser) return;
    
    setIsUnfriending(true);
    try {
      await removeFriend(unfriendingUser.id);
      setUnfriendingUser(null);
    } catch (error) {
      console.error('Error removing friend:', error);
    } finally {
      setIsUnfriending(false);
    }
  };

  const pendingRequestsCount = friendRequests.incoming.length;

  // Show signup prompt for non-logged-in users
  if (!user) {
    return (
      <div className="min-h-screen bg-dive-black py-6 pb-24">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center py-16 bg-dive-dark rounded-xl border border-dive-gray">
            <Users className="w-16 h-16 text-dive-blue mx-auto mb-4" />
            <h1 className="text-2xl font-display text-white mb-3">Connect with Friends</h1>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Sign in to add friends, see what bars they're rating, and share your dive bar adventures together!
            </p>
            <Link
              to="/auth"
              className="btn-primary inline-flex items-center gap-2"
            >
              <LogIn className="w-5 h-5" />
              Sign In or Create Account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dive-black py-6 pb-24">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-dive-blue/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-dive-blue" />
            </div>
            <div>
              <h1 className="font-display text-2xl text-white tracking-wide">SOCIAL</h1>
              <p className="text-gray-400 text-sm">{friends.length} following</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NearbyCheckInButton variant="icon" />
            {/* Requests button - always visible */}
            <button
              onClick={() => setActiveTab('requests')}
              className={`relative p-3 rounded-xl transition-all ${
                activeTab === 'requests' ? 'bg-dive-amber text-white' : 'bg-dive-dark text-gray-400 hover:text-white'
              }`}
            >
              <UserCheck className="w-5 h-5" />
              {(pendingRequestsCount > 0 || followRequests?.incoming?.length > 0) && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-dive-amber text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                  {pendingRequestsCount + (followRequests?.incoming?.length || 0)}
                </span>
              )}
            </button>
            {/* Add friend button */}
            <button
              onClick={() => setShowSearch(!showSearch)}
              className={`p-3 rounded-xl transition-all ${
                showSearch ? 'bg-dive-neon text-white' : 'bg-dive-dark text-gray-400 hover:text-white'
              }`}
            >
              <UserPlus className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Follow Requests Banner */}
        {followRequests?.incoming?.length > 0 && (
          <button
            onClick={() => setActiveTab('requests')}
            className="w-full mb-4 p-4 bg-dive-amber/20 border border-dive-amber/50 rounded-xl flex items-center justify-between hover:bg-dive-amber/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-dive-amber/30 rounded-full flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-dive-amber" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium">
                  {followRequests.incoming.length} Follow Request{followRequests.incoming.length > 1 ? 's' : ''}
                </p>
                <p className="text-gray-400 text-sm">Tap to view and respond</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-dive-amber" />
          </button>
        )}

        {/* Add Friends Search */}
        {showSearch && (
          <div className="mb-6 bg-dive-dark border border-dive-gray rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <UserPlus className="w-5 h-5 text-dive-neon" />
              <h3 className="text-white font-medium">Find Friends</h3>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search by username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10"
              />
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-3 space-y-2">
                {searchResults.slice(0, 5).map(searchUser => {
                  const status = getFollowStatus ? getFollowStatus(searchUser.id) : getFriendStatus(searchUser.id);
                  const isFollowing = following.includes(searchUser.id);
                  const isPending = followRequests?.outgoing?.some(r => r.following_id === searchUser.id);
                  return (
                    <div
                      key={searchUser.id}
                      className="flex items-center justify-between p-2 bg-dive-darker rounded-lg"
                    >
                      <Link 
                        to={`/social/${searchUser.id}`}
                        className="flex items-center gap-2 flex-1 hover:opacity-80 transition-opacity"
                        onClick={() => setShowSearch(false)}
                      >
                        <div className="w-8 h-8 rounded-full bg-dive-neon/20 flex items-center justify-center overflow-hidden">
                          {searchUser.avatarUrl ? (
                            <img src={searchUser.avatarUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-dive-neon text-sm">
                              {searchUser.displayName.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="text-white text-sm">{searchUser.displayName}</p>
                          <p className="text-gray-500 text-xs">@{searchUser.username}</p>
                        </div>
                      </Link>
                      {!isFollowing && !isPending && (
                        <button
                          onClick={() => handleFollow(searchUser.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-dive-neon text-white text-sm rounded-lg hover:bg-dive-neon/80"
                        >
                          <UserPlus className="w-4 h-4" />
                          Follow
                        </button>
                      )}
                      {isPending && (
                        <span className="text-gray-500 text-sm flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Requested
                        </span>
                      )}
                      {isFollowing && (
                        <span className="text-dive-neon text-sm flex items-center gap-1">
                          <UserCheck className="w-4 h-4" />
                          Following
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            
            {searchQuery && searchResults.length === 0 && (
              <p className="text-gray-500 text-sm mt-3 text-center">
                No users found matching "{searchQuery}"
              </p>
            )}
          </div>
        )}

        {/* Tabs - Styled like Bars page */}
        <div className="flex bg-dive-darker rounded-lg p-1 mb-4">
          <button
            onClick={() => setActiveTab('feed')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'feed'
                ? 'bg-dive-neon text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Heart className="w-4 h-4" />
            Following
          </button>
          <button
            onClick={() => setActiveTab('myfeed')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              activeTab === 'myfeed'
                ? 'bg-amber-500 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Star className="w-4 h-4" />
            My Feed
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'feed' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                  showFilters || selectedFriend !== 'all' || diveBarOnly || timeframe !== 'all'
                    ? 'bg-dive-neon text-white'
                    : 'bg-dive-dark text-gray-400 hover:text-white'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
                {(selectedFriend !== 'all' || diveBarOnly || timeframe !== 'all') && (
                  <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">
                    {[selectedFriend !== 'all', diveBarOnly, timeframe !== 'all'].filter(Boolean).length}
                  </span>
                )}
              </button>
              
              {/* Quick Dive Bar Toggle - Gold outline style like My Rated button */}
              <button
                onClick={() => setDiveBarOnly(!diveBarOnly)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                  diveBarOnly
                    ? 'border-2 border-amber-400 bg-amber-500/20 text-amber-400'
                    : 'bg-dive-dark text-gray-400 hover:text-white border border-dive-gray'
                }`}
              >
                Dive Bars Only
              </button>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="bg-dive-dark border border-dive-gray rounded-xl p-4 space-y-4">
                {/* User Filter */}
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Filter by User</label>
                  <select
                    value={selectedFriend}
                    onChange={(e) => setSelectedFriend(e.target.value)}
                    className="w-full bg-dive-darker border border-dive-gray rounded-lg px-3 py-2 text-white"
                  >
                    <option value="all">All Following</option>
                    {friendUsers.map(friend => (
                      <option key={friend.id} value={friend.id}>
                        {friend.displayName} (@{friend.username})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Timeframe Filter */}
                <div>
                  <label className="text-gray-400 text-sm mb-2 block">Time Period</label>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { value: 'all', label: 'All Time' },
                      { value: '24h', label: 'Last 24 Hours' },
                      { value: 'week', label: 'Last Week' },
                      { value: 'month', label: 'Last Month' }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => setTimeframe(option.value)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                          timeframe === option.value
                            ? 'bg-dive-neon text-white'
                            : 'bg-dive-darker text-gray-400 hover:text-white'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Clear Filters */}
                {(selectedFriend !== 'all' || diveBarOnly || timeframe !== 'all') && (
                  <button
                    onClick={() => {
                      setSelectedFriend('all');
                      setDiveBarOnly(false);
                      setTimeframe('all');
                    }}
                    className="text-dive-neon text-sm hover:underline"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            )}

            {/* Feed Results */}
            {loadingFeed ? (
              <div className="text-center py-8 text-gray-500">Loading feed...</div>
            ) : filteredFeed.length === 0 ? (
              <div className="text-center py-12 bg-dive-dark border border-dive-gray rounded-xl">
                <Heart className="w-12 h-12 text-dive-gray mx-auto mb-3" />
                <h3 className="text-white text-lg mb-2">
                  {friendFeed.length === 0 ? 'No activity yet' : 'No matching ratings'}
                </h3>
                <p className="text-gray-500 text-sm mb-4">
                  {friends.length === 0
                    ? 'Add some friends to see their ratings here!'
                    : friendFeed.length === 0
                      ? "Your friends haven't rated any bars yet."
                      : 'Try adjusting your filters to see more results.'
                  }
                </p>
                {friends.length === 0 && (
                  <button
                    onClick={() => setShowSearch(true)}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    <UserPlus className="w-4 h-4" />
                    Find Friends
                  </button>
                )}
              </div>
            ) : (
              <>
                <p className="text-gray-500 text-sm">
                  Showing {filteredFeed.length} item{filteredFeed.length !== 1 ? 's' : ''}
                  {(selectedFriend !== 'all' || diveBarOnly || timeframe !== 'all') && ' (filtered)'}
                </p>
                {filteredFeed.map(item => (
                  item.type === 'badge' ? (
                    <BadgeFeedItem key={`badge-${item.id}`} item={item} />
                  ) : item.type === 'check_in' ? (
                    <CheckInFeedItem key={`checkin-${item.check_in_id}`} item={item} />
                  ) : (
                    <FeedItem key={item.rating_id} item={item} bars={bars} photos={photos} />
                  )
                ))}
              </>
            )}
          </div>
        )}

        {/* My Feed Tab - Shows my bars with friends' comments */}
        {activeTab === 'myfeed' && (
          <MyFeedTab bars={bars} ratings={ratings} user={user} photos={photos} comments={comments} />
        )}

        {activeTab === 'requests' && (
          <div className="space-y-4">
            {/* Incoming Follow Requests (for private accounts) */}
            {followRequests?.incoming?.length > 0 && (
              <div>
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-purple-400" />
                  Follow Requests ({followRequests.incoming.length})
                </h3>
                <div className="space-y-2">
                  {followRequests.incoming.map(request => (
                    <div key={request.follower_id} className="bg-dive-dark border border-dive-gray rounded-xl p-4 flex items-center gap-3">
                      <Link to={`/social/${request.follower_id}`} className="w-12 h-12 rounded-full bg-dive-neon/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {request.from_user?.avatar_url ? (
                          <img src={request.from_user.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-dive-neon font-medium text-lg">
                            {(request.from_user?.display_name || 'U').charAt(0).toUpperCase()}
                          </span>
                        )}
                      </Link>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{request.from_user?.display_name || 'User'}</p>
                        <p className="text-gray-500 text-sm">@{request.from_user?.username}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => denyFollowRequest(request.follower_id)}
                          className="p-2 bg-dive-gray text-gray-400 rounded-lg hover:bg-red-500/20 hover:text-red-400"
                        >
                          <X className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => acceptFollowRequest(request.follower_id)}
                          className="p-2 bg-dive-neon text-white rounded-lg"
                        >
                          <Check className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Incoming Follow Requests */}
            {!followRequests?.incoming?.length && (
              <div className="mb-6">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-dive-amber" />
                  Incoming Requests
                </h3>
                <p className="text-gray-500 text-sm bg-dive-dark border border-dive-gray rounded-xl p-4 text-center">
                  No pending requests
                </p>
              </div>
            )}

            {/* Outgoing Follow Requests */}
            <div>
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-500" />
                Sent Requests ({followRequests?.outgoing?.length || 0})
              </h3>
              {followRequests?.outgoing?.length > 0 ? (
                <div className="space-y-2">
                  {followRequests.outgoing.map(request => {
                    const targetUser = users.find(u => u.id === request.following_id);
                    return (
                      <div key={request.following_id} className="bg-dive-dark border border-dive-gray rounded-xl p-4 flex items-center gap-3">
                        <Link to={`/social/${request.following_id}`} className="w-12 h-12 rounded-full bg-dive-neon/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {targetUser?.avatarUrl ? (
                            <img src={targetUser.avatarUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-dive-neon font-medium text-lg">
                              {(targetUser?.displayName || 'U').charAt(0).toUpperCase()}
                            </span>
                          )}
                        </Link>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium truncate">{targetUser?.displayName || 'User'}</p>
                          <p className="text-gray-500 text-sm">@{targetUser?.username}</p>
                        </div>
                        <span className="text-gray-500 text-sm flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          Pending
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-sm bg-dive-dark border border-dive-gray rounded-xl p-4 text-center">
                  No sent requests
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Unfriend Confirmation Modal */}
      {unfriendingUser && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-dive-dark border border-dive-gray rounded-xl max-w-sm w-full p-6">
            <h3 className="text-white text-lg font-medium mb-2">
              Unfriend {unfriendingUser.displayName}?
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              You will no longer see each other's activity or ratings.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setUnfriendingUser(null)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveFriend}
                disabled={isUnfriending}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                {isUnfriending ? 'Removing...' : 'Unfriend'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Content Modal */}
      <ReportModal
        isOpen={!!reportingContent}
        onClose={() => setReportingContent(null)}
        contentType={reportingContent?.type || 'feed_comment'}
        contentId={reportingContent?.id}
        contentPreview={reportingContent?.text}
      />
    </div>
  );
}

export default Friends;
